// Vendor
import React, { useCallback, useEffect, useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

// Components
import { DropdownMultiSelect } from 'src/components/molecules/DropdownMultiSelect';

// Hooks
import usePropertiesDDown from 'src/features/property/hooks/usePropertiesDDown';
import { useScrollObserver } from 'src/hooks';

// Helpers
import { calculatePagesCount, formatCheckboxData } from 'src/helpers';

// Types
import { CheckboxType } from 'src/components/atoms/Checkbox/types';
import { IPaginationRequest } from 'src/ts/interfaces';

type CallbackParams = {
  ids: string;
  data: CheckboxType[];
  isCheckAll: boolean;
  q?: string;
};

type PropertyMultiSelectorProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  methods: UseFormReturn<any>;
  isDisabled: boolean;
  onCallback: ({ ids, data, isCheckAll, q }: CallbackParams) => void;
  onApply: () => void;
};

const PropertyMultiSelector: React.FC<PropertyMultiSelectorProps> = ({
  methods,
  isDisabled,
  onCallback,
  onApply
}) => {
  const { onGetPropertiesDDown, getPropertiesDDown: propertiesDDown } = usePropertiesDDown();

  const {
    data: propertyData,
    isIdle: propertyIsIdle,
    isLoading: propertyIsLoading,
    count: propertyCount,
    filter: propertyFilter,
    error: propertyError
  } = propertiesDDown;

  const dataFilter: IPaginationRequest = useMemo(
    () => ({
      ...propertyFilter,
      page: 0
    }),
    [propertyFilter]
  );

  useEffect(() => {
    if (!isDisabled && propertyIsIdle) {
      onGetPropertiesDDown(dataFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDisabled, propertyIsIdle]);

  const { scrollObserver: getPropertyScrollObserver } = useScrollObserver({
    page: propertyFilter.page,
    isLoading: propertyIsLoading,
    callback: (page: number) => {
      onGetPropertiesDDown({ ...dataFilter, page });
    },
    hasMore: propertyFilter.page < calculatePagesCount(propertyFilter.rowsPerPage, propertyCount),
    dependency: [propertyIsLoading, propertyFilter]
  });

  const formattedData = useMemo(
    () =>
      formatCheckboxData({
        data: propertyData || [],
        value: 'id',
        label: 'name'
      }),
    [propertyData]
  );

  const handleCallback = useCallback(
    (params: CallbackParams) => {
      if (params.isCheckAll) {
        onGetPropertiesDDown({
          ...dataFilter,
          rowsPerPage: propertyCount,
          q: params.q
        });
      }

      onCallback(params);
    },
    [propertyCount, dataFilter, onCallback, onGetPropertiesDDown]
  );

  return (
    <Controller
      key={`property_dropdown`}
      name="property_id"
      control={methods.control}
      render={({ field: { name } }) => (
        <DropdownMultiSelect
          data={formattedData}
          totalRows={propertyCount}
          onChange={handleCallback}
          onApply={onApply}
          name={name}
          size="md"
          itemRef={getPropertyScrollObserver}
          isLoading={propertyIsLoading}
          isDisabled={isDisabled || propertyError !== null}
          showSearch
          onSearch={(q) => {
            onGetPropertiesDDown({ ...dataFilter, q });
          }}
          textSelectAll="All Properties"
        />
      )}
    />
  );
};

export default PropertyMultiSelector;
